import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { FaEdit, FaTimes, FaFilePdf, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import AssignmentService from "../../../../services/assignmentService";

const EditAssignmentModal = ({ show, onHide, assignment, onSuccess }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [totalMarks, setTotalMarks] = useState(100);
  const [status, setStatus] = useState("draft");
  const [attachment, setAttachment] = useState(null);
  const [removeExistingAttachment, setRemoveExistingAttachment] =
    useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (assignment) {
      console.log("Editing assignment:", assignment);
      setTitle(assignment.title || "");
      setDescription(assignment.description || "");
      // Format date for datetime-local input
      if (assignment.due_date) {
        const date = new Date(assignment.due_date);
        if (!isNaN(date.getTime())) {
          const formattedDate = date.toISOString().slice(0, 16);
          setDueDate(formattedDate);
        } else {
          setDueDate("");
        }
      } else {
        setDueDate("");
      }
      setTotalMarks(assignment.total_marks || 100);
      setStatus(assignment.status || "draft");
      setAttachment(null);
      setRemoveExistingAttachment(false);
    }
  }, [assignment]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!dueDate) {
      toast.error("Due date is required");
      return;
    }

    const formData = new FormData();
    formData.append("_method", "PUT"); // IMPORTANT: Add this FIRST
    formData.append("title", title);
    formData.append("description", description);
    formData.append("due_date", dueDate);
    formData.append("total_marks", totalMarks);
    formData.append("status", status);

    // Add remove attachment flag
    if (removeExistingAttachment) {
      formData.append("remove_attachment", "1");
    }

    if (attachment) {
      formData.append("attachment", attachment);
    }

    // Debug: Log FormData contents
    console.log("Submitting update for assignment ID:", assignment.id);
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    try {
      setLoading(true);
      await AssignmentService.updateAssignment(assignment.id, formData);
      toast.success("Assignment updated successfully");
      onSuccess();
      onHide();
    } catch (error) {
      console.error("Failed to update assignment:", error);
      toast.error(
        error.response?.data?.message || "Failed to update assignment",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!assignment) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEdit className="me-2 text-warning" />
          Edit Assignment
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter assignment title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description / Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              placeholder="Describe the assignment requirements, guidelines, etc."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Due Date & Time <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Total Marks</Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="1000"
              value={totalMarks}
              onChange={(e) => setTotalMarks(parseInt(e.target.value) || 0)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Draft (Not visible to students)"
                name="status"
                value="draft"
                checked={status === "draft"}
                onChange={(e) => setStatus(e.target.value)}
              />
              <Form.Check
                inline
                type="radio"
                label="Published (Visible to students)"
                name="status"
                value="published"
                checked={status === "published"}
                onChange={(e) => setStatus(e.target.value)}
              />
              <Form.Check
                inline
                type="radio"
                label="Closed (No more submissions)"
                name="status"
                value="closed"
                checked={status === "closed"}
                onChange={(e) => setStatus(e.target.value)}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Attachment (PDF)</Form.Label>
            {assignment.attachment_name &&
              !removeExistingAttachment &&
              !attachment && (
                <Alert variant="info" className="py-2">
                  <FaFilePdf className="me-2" />
                  Current file: {assignment.attachment_name}
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger float-end"
                    onClick={() => setRemoveExistingAttachment(true)}
                  >
                    <FaTrash /> Remove
                  </Button>
                </Alert>
              )}
            {removeExistingAttachment && !attachment && (
              <Alert variant="warning" className="py-2">
                <FaTrash className="me-2" />
                Existing attachment will be removed
                <Button
                  variant="link"
                  size="sm"
                  className="text-success float-end"
                  onClick={() => setRemoveExistingAttachment(false)}
                >
                  Cancel Remove
                </Button>
              </Alert>
            )}
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={(e) => setAttachment(e.target.files[0])}
            />
            {attachment && (
              <div className="mt-2">
                <Alert variant="success" className="py-1">
                  <FaFilePdf className="me-1" /> New file: {attachment.name}
                </Alert>
              </div>
            )}
            <Form.Text className="text-muted">
              Upload new PDF to replace existing (optional). Max 10MB.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          <FaTimes className="me-2" /> Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            <>
              <FaEdit className="me-2" /> Save Changes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAssignmentModal;
